/**
 * Market (市场) - Trading post with ornate design
 * Features: Red and gold curved roof, warm interior glow, open-sided stall, decorative banners
 */

import * as THREE from 'three';
import { createLantern } from '../BuildingMaterials';

export function createMarketMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x7a7a7a, roughness: 0.9 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.8 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.85 });
  const redMat = new THREE.MeshStandardMaterial({ color: 0xb71c1c, roughness: 0.7 });
  const darkRedMat = new THREE.MeshStandardMaterial({ color: 0x7f0000, roughness: 0.6 });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xffd700, roughness: 0.3, metalness: 0.6 });
  const lanternMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.5
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.6
  });

  // === Stone Platform Base ===
  const baseGeom1 = new THREE.BoxGeometry(2.8, 0.18, 2.6);
  const base1 = new THREE.Mesh(baseGeom1, stoneMat);
  base1.position.y = 0.09;
  base1.receiveShadow = true;
  base1.castShadow = true;
  group.add(base1);

  // Elevated wooden platform
  const platformGeom = new THREE.BoxGeometry(2.5, 0.12, 2.3);
  const platformMat = new THREE.MeshStandardMaterial({ color: 0x6d5c4a, roughness: 0.85 });
  const platform = new THREE.Mesh(platformGeom, platformMat);
  platform.position.y = 0.24;
  platform.receiveShadow = true;
  group.add(platform);

  // Steps
  for (let i = 0; i < 2; i++) {
    const stepGeom = new THREE.BoxGeometry(0.7, 0.08, 0.2);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0, 0.04 + i * 0.1, 1.2 + i * 0.15);
    step.castShadow = true;
    group.add(step);
  }

  // === Main Building Structure ===
  // Back wall
  const backWallGeom = new THREE.BoxGeometry(2.0, 1.1, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(0, 0.88, -1.0);
  backWall.castShadow = true;
  group.add(backWall);

  // Partial side walls
  const sideWallGeom = new THREE.BoxGeometry(0.12, 1.1, 0.8);
  const leftWall = new THREE.Mesh(sideWallGeom, darkWoodMat);
  leftWall.position.set(-1.0, 0.88, -0.55);
  leftWall.castShadow = true;
  group.add(leftWall);

  const rightWall = new THREE.Mesh(sideWallGeom.clone(), darkWoodMat);
  rightWall.position.set(1.0, 0.88, -0.55);
  rightWall.castShadow = true;
  group.add(rightWall);

  // === Ornate Support Pillars ===
  const pillarGeom = new THREE.CylinderGeometry(0.06, 0.08, 1.15, 8);
  const pillarPositions = [
    { x: -1.0, z: 0.1 },
    { x: 1.0, z: 0.1 },
    { x: -1.0, z: 0.8 },
    { x: 1.0, z: 0.8 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, redMat);
    pillar.position.set(pos.x, 0.87, pos.z);
    pillar.castShadow = true;
    group.add(pillar);

    // Gold pillar base
    const baseRingGeom = new THREE.CylinderGeometry(0.1, 0.12, 0.08, 8);
    const baseRing = new THREE.Mesh(baseRingGeom, goldMat);
    baseRing.position.set(pos.x, 0.34, pos.z);
    group.add(baseRing);

    // Gold pillar top
    const topRingGeom = new THREE.CylinderGeometry(0.1, 0.08, 0.06, 8);
    const topRing = new THREE.Mesh(topRingGeom, goldMat);
    topRing.position.set(pos.x, 1.42, pos.z);
    group.add(topRing);
  }

  // === Red and Gold Curved Roof ===
  // Main roof
  const roofBaseGeom = new THREE.BoxGeometry(2.6, 0.15, 2.4);
  const roofBase = new THREE.Mesh(roofBaseGeom, redMat);
  roofBase.position.y = 1.52;
  roofBase.castShadow = true;
  group.add(roofBase);

  // Curved roof top (cone shape)
  const roofConeGeom = new THREE.ConeGeometry(1.6, 0.8, 4);
  const roofCone = new THREE.Mesh(roofConeGeom, redMat);
  roofCone.position.y = 1.95;
  roofCone.rotation.y = Math.PI / 4;
  roofCone.castShadow = true;
  group.add(roofCone);

  // Gold roof edge (eave)
  const eaveGeom = new THREE.BoxGeometry(2.8, 0.08, 2.6);
  const eave = new THREE.Mesh(eaveGeom, goldMat);
  eave.position.y = 1.47;
  group.add(eave);

  // Gold ridge on top
  const ridgeGeom = new THREE.BoxGeometry(0.12, 0.12, 1.8);
  const ridge = new THREE.Mesh(ridgeGeom, goldMat);
  ridge.position.y = 2.35;
  group.add(ridge);

  // Gold corner decorations
  const cornerOrnamentGeom = new THREE.ConeGeometry(0.08, 0.15, 4);
  const cornerPositions = [
    { x: 1.35, z: 1.25 }, { x: -1.35, z: 1.25 },
    { x: 1.35, z: -1.15 }, { x: -1.35, z: -1.15 }
  ];
  for (const pos of cornerPositions) {
    const ornament = new THREE.Mesh(cornerOrnamentGeom, goldMat);
    ornament.position.set(pos.x, 1.4, pos.z);
    ornament.rotation.x = Math.PI;
    group.add(ornament);
  }

  // Top ornament
  const topOrnamentGeom = new THREE.SphereGeometry(0.12, 8, 6);
  const topOrnament = new THREE.Mesh(topOrnamentGeom, goldMat);
  topOrnament.position.y = 2.45;
  group.add(topOrnament);

  // === Display Counter ===
  const counterGeom = new THREE.BoxGeometry(1.6, 0.12, 0.5);
  const counter = new THREE.Mesh(counterGeom, woodMat);
  counter.position.set(0, 0.6, 0.7);
  group.add(counter);

  // Counter front decoration
  const counterFrontGeom = new THREE.BoxGeometry(1.6, 0.35, 0.06);
  const counterFront = new THREE.Mesh(counterFrontGeom, redMat);
  counterFront.position.set(0, 0.45, 0.93);
  group.add(counterFront);

  // Gold trim on counter
  const trimGeom = new THREE.BoxGeometry(1.7, 0.04, 0.08);
  const trim = new THREE.Mesh(trimGeom, goldMat);
  trim.position.set(0, 0.67, 0.72);
  group.add(trim);

  // === Goods on Display ===
  // Gold coins/ingots
  const ingotGeom = new THREE.BoxGeometry(0.08, 0.04, 0.06);
  for (let i = 0; i < 6; i++) {
    const ingot = new THREE.Mesh(ingotGeom, goldMat);
    ingot.position.set(-0.5 + i * 0.18, 0.68, 0.6);
    ingot.rotation.y = Math.random() * 0.3;
    group.add(ingot);
  }

  // Pottery/vases
  const vaseGeom = new THREE.CylinderGeometry(0.05, 0.07, 0.15, 8);
  const vaseMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7 });
  for (let i = 0; i < 3; i++) {
    const vase = new THREE.Mesh(vaseGeom, vaseMat);
    vase.position.set(-0.4 + i * 0.35, 0.74, 0.75);
    group.add(vase);
  }

  // === Red Lanterns ===
  createLantern(group, -0.9, 1.25, 0.8, lanternMat);
  createLantern(group, 0.9, 1.25, 0.8, lanternMat);

  // === Interior Glow ===
  const glowPlaneGeom = new THREE.PlaneGeometry(0.8, 0.7);
  const glowPlane = new THREE.Mesh(glowPlaneGeom, glowMat);
  glowPlane.position.set(0, 0.75, -0.92);
  group.add(glowPlane);

  // === Decorative Banners ===
  const bannerGeom = new THREE.PlaneGeometry(0.25, 0.6);
  const bannerMat = new THREE.MeshStandardMaterial({ color: 0xc62828, side: THREE.DoubleSide, roughness: 0.8 });

  const banner1 = new THREE.Mesh(bannerGeom, bannerMat);
  banner1.position.set(-0.5, 1.0, 0.95);
  group.add(banner1);

  const banner2 = new THREE.Mesh(bannerGeom.clone(), bannerMat);
  banner2.position.set(0.5, 1.0, 0.95);
  group.add(banner2);

  // Gold characters on banners (simple rectangles)
  const charGeom = new THREE.PlaneGeometry(0.12, 0.15);
  const charMat = new THREE.MeshStandardMaterial({ color: 0xffd700, side: THREE.DoubleSide });
  const char1 = new THREE.Mesh(charGeom, charMat);
  char1.position.set(-0.5, 1.05, 0.96);
  group.add(char1);

  const char2 = new THREE.Mesh(charGeom.clone(), charMat);
  char2.position.set(0.5, 1.05, 0.96);
  group.add(char2);

  // === Fabric Awning ===
  const awningGeom = new THREE.BoxGeometry(2.0, 0.04, 0.6);
  const awningMat = new THREE.MeshStandardMaterial({ color: 0xc62828, roughness: 0.8 });
  const awning = new THREE.Mesh(awningGeom, awningMat);
  awning.position.set(0, 1.38, 0.95);
  awning.rotation.x = 0.15;
  awning.castShadow = true;
  group.add(awning);

  // Awning gold fringe
  const fringeGeom = new THREE.BoxGeometry(2.0, 0.06, 0.02);
  const fringe = new THREE.Mesh(fringeGeom, goldMat);
  fringe.position.set(0, 1.32, 1.2);
  group.add(fringe);

  // === Crates and Sacks ===
  const crateGeom = new THREE.BoxGeometry(0.25, 0.22, 0.25);
  const crateMat = new THREE.MeshStandardMaterial({ color: 0x6d5c4a, roughness: 0.85 });

  const crate1 = new THREE.Mesh(crateGeom, crateMat);
  crate1.position.set(-0.75, 0.41, 0.1);
  group.add(crate1);

  const crate2 = new THREE.Mesh(crateGeom.clone(), crateMat);
  crate2.position.set(-0.75, 0.63, 0.1);
  crate2.rotation.y = 0.2;
  group.add(crate2);

  // Sack
  const sackGeom = new THREE.SphereGeometry(0.15, 8, 6);
  sackGeom.scale(1.0, 0.8, 0.9);
  const sackMat = new THREE.MeshStandardMaterial({ color: 0xc9a66b, roughness: 0.9 });
  const sack = new THREE.Mesh(sackGeom, sackMat);
  sack.position.set(0.75, 0.42, 0.2);
  group.add(sack);

  // === Hanging Sign ===
  const signPostGeom = new THREE.BoxGeometry(0.04, 0.3, 0.04);
  const signPost = new THREE.Mesh(signPostGeom, woodMat);
  signPost.position.set(0, 1.8, 0.9);
  signPost.rotation.x = Math.PI / 4;
  group.add(signPost);

  const signGeom = new THREE.BoxGeometry(0.4, 0.25, 0.03);
  const signMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.8 });
  const sign = new THREE.Mesh(signGeom, signMat);
  sign.position.set(0, 1.55, 1.0);
  group.add(sign);

  // Gold border on sign
  const signBorderGeom = new THREE.BoxGeometry(0.44, 0.02, 0.04);
  const signBorder1 = new THREE.Mesh(signBorderGeom, goldMat);
  signBorder1.position.set(0, 1.66, 1.0);
  group.add(signBorder1);

  const signBorder2 = new THREE.Mesh(signBorderGeom.clone(), goldMat);
  signBorder2.position.set(0, 1.44, 1.0);
  group.add(signBorder2);
}
